import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useCredits } from "../contexts/CreditContext";
import { ShoppingCart, Sparkles, Check, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuyCredits() {
  const { token } = useAuth();
  const ctx = useCredits();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/credits/packs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setPacks(d.packs || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("credit_session_id");
    if (!sessionId || !token) return;

    // Clean URL
    window.history.replaceState({}, "", "/buy-credits");

    fetch(`${API}/credits/confirm`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          toast.success(`${d.credits_added} credits added to your account!`);
          ctx?.refresh();
        } else {
          toast.error(d.message || "Purchase verification failed");
        }
      })
      .catch(() => toast.error("Failed to verify purchase"));
  }, [token, ctx]);

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const currentUrl = window.location.origin + "/buy-credits";
      const res = await fetch(`${API}/credits/purchase`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pack_id: packId,
          success_url: currentUrl,
          cancel_url: currentUrl,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Failed to create checkout");
        setPurchasing(null);
      }
    } catch {
      toast.error("Purchase failed");
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="buy-credits-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6"
          onClick={() => navigate("/dashboard")}
          data-testid="buy-credits-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="text-center mb-10">
          <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <ShoppingCart className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Buy AI Credits</h1>
          <p className="text-muted-foreground mt-2 text-base">
            Top up your AI credits instantly. Purchased credits persist across billing cycles.
          </p>
          {ctx && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border text-sm" data-testid="buy-credits-balance">
              <Sparkles className="w-4 h-4 text-primary" />
              <span>Current balance:</span>
              <span className="font-semibold">{ctx.monthlyRemaining} monthly</span>
              {ctx.purchasedRemaining > 0 && (
                <>
                  <span className="text-muted-foreground">+</span>
                  <span className="font-semibold text-emerald-400">{ctx.purchasedRemaining} purchased</span>
                </>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-12">Loading packs...</div>
        ) : packs.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">Credit packs are not available at this time.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packs.map((pack, i) => {
              const isPopular = i === 1;
              return (
                <div
                  key={pack.id}
                  className={`relative rounded-xl border p-6 flex flex-col items-center transition-all hover:border-primary/50 hover:shadow-lg ${
                    isPopular ? "border-primary bg-primary/5 shadow-md" : "bg-card"
                  }`}
                  data-testid={`credit-pack-${pack.id}`}
                >
                  {isPopular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      Best Value
                    </span>
                  )}
                  <h3 className="text-lg font-semibold mt-2">{pack.label}</h3>
                  <div className="mt-3 text-4xl font-bold tracking-tight">
                    ${pack.price_usd}
                  </div>
                  <p className="text-muted-foreground text-sm mt-1">
                    {pack.credits.toLocaleString()} credits
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ${pack.price_per_credit}/credit
                  </p>
                  <ul className="mt-4 space-y-2 text-sm w-full">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span>No expiration</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span>Used after monthly credits</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span>Instant activation</span>
                    </li>
                  </ul>
                  <Button
                    className="w-full mt-6"
                    variant={isPopular ? "default" : "outline"}
                    disabled={purchasing === pack.id}
                    onClick={() => handlePurchase(pack.id)}
                    data-testid={`buy-pack-${pack.id}`}
                  >
                    {purchasing === pack.id ? "Redirecting..." : `Buy ${pack.credits} Credits`}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 text-center text-xs text-muted-foreground max-w-lg mx-auto">
          <p>
            Purchased credits are consumed only after your monthly allocation is used.
            They persist across billing cycles and do not expire.
            Payments are processed securely via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
